import { Localized } from "@fluent/react/compat";
import React, { FunctionComponent, useCallback } from "react";
import { useField } from "react-final-form";
import { graphql } from "react-relay";

import {
  FieldSet,
  FormField,
  FormFieldDescription,
  FormFieldHeader,
  HorizontalGutter,
  Label,
} from "coral-ui/components/v2";

import SiteSearch from "coral-admin/components/SiteSearch";
import ConfigBox from "../../ConfigBox";
import Header from "../../Header";
import HelperText from "../../HelperText";
import OnOffField from "../../OnOffField";
import SpecificSitesSelectedQuery from "./SpecificSitesSelectedQuery";

// eslint-disable-next-line no-unused-expressions
graphql`
  fragment OptionalRatingsConfig_formValues on Settings {
    enableOptionalRatings
    enableOptionalRatingsSites
  }
`;

interface Props {
  disabled: boolean;
}

const OptionalRatingsConfig: FunctionComponent<Props> = ({ disabled }) => {
  const { input: enableOptionalRatingsInput } = useField<boolean>(
    "enableOptionalRatings"
  );
  const { input: enableOptionalRatingsSitesInput } = useField<string[]>(
    "enableOptionalRatingsSites"
  );

  const onAddSite = useCallback(
    (siteID: string) => {
      const currentValue = enableOptionalRatingsSitesInput.value || [];
      const changed = [...currentValue];
      const index = changed.indexOf(siteID);
      if (index === -1) {
        changed.push(siteID);
      }
      enableOptionalRatingsSitesInput.onChange(changed);
    },
    [enableOptionalRatingsSitesInput]
  );

  const onRemoveSite = useCallback(
    (siteID: string) => {
      const currentValue = enableOptionalRatingsSitesInput.value || [];
      const changed = [...currentValue];
      const index = changed.indexOf(siteID);
      if (index >= 0) {
        changed.splice(index, 1);
      }
      enableOptionalRatingsSitesInput.onChange(changed);
    },
    [enableOptionalRatingsSitesInput]
  );

  return (
    <ConfigBox
      title={
        <Localized id="configure-moderation-ratings-title">
          <Header container={<legend />}>Ratings</Header>
        </Localized>
      }
      container={<FieldSet />}
    >
      <Localized id="configure-moderation-optionalRatings-description">
        <FormFieldDescription>
          Configure rating functionality.
        </FormFieldDescription>
      </Localized>
      <FormField container={<FieldSet />}>
        <FormFieldHeader>
          <Localized id="configure-moderation-optionalRatings-enabled">
            <Label component="legend">Enable optional ratings</Label>
          </Localized>
          <Localized id="configure-moderation-optionalRatings-enabled-description">
            <HelperText>
              When enabled, users can submit rating-only reviews.
            </HelperText>
          </Localized>
        </FormFieldHeader>
        <OnOffField name="enableOptionalRatings" disabled={disabled} />

        {enableOptionalRatingsInput.value === true && (
          <div style={{ marginTop: "1rem" }}>
            <Localized id="configure-moderation-optionalRatings-sites">
              <Label>Select sites where optional ratings are enabled</Label>
            </Localized>
            <HorizontalGutter spacing={3} mt={3} mb={3}>
              {(enableOptionalRatingsSitesInput.value || []).map(
                (siteID: string) => {
                  return (
                    <SpecificSitesSelectedQuery
                      key={siteID}
                      siteID={siteID}
                      onChange={onRemoveSite}
                    />
                  );
                }
              )}
            </HorizontalGutter>
            <SiteSearch
              showOnlyScopedSitesInSearchResults
              showAllSitesSearchFilterOption={false}
              showSiteSearchLabel={false}
              onSelect={onAddSite}
              clearTextFieldValueAfterSelect={true}
            />
          </div>
        )}
      </FormField>
    </ConfigBox>
  );
};

export default OptionalRatingsConfig;
